/**
 * Read-only analytics over the consumer Skida `users` collection.
 *
 * Aggregation queries only (`getCountFromServer`) — a count returns a number
 * and nothing else. With ~50k user documents, reading them to count them would
 * be both slow and an unnecessary spread of personal data.
 *
 * Field notes, verified against live data rather than assumed:
 *   createdAt                       epoch milliseconds. Present broadly.
 *   activeEntitlements.Premium      the consumer plan.
 *   …Premium.expirationDateMillis   epoch ms. **This is what decides validity.**
 *   …Premium.periodType             "NORMAL" (paying) or "TRIAL".
 *   entitlementStatus / promoAccess Skida PRO promo signals on 5 accounts only
 *                                   — NOT a consumer plan.
 *
 * ⚠️ **Never filter on `activeEntitlements.Premium.isActive`.** It is a
 * write-time snapshot that is never flipped back: 3,002 documents have
 * `isActive: true` and **zero** have `false`. It therefore means "has ever held
 * Premium", not "holds Premium now" — 1,392 of those 3,002 had already expired.
 * Validity is `expirationDateMillis > Date.now()` and nothing else.
 */
import { collection, getCountFromServer, query, where } from 'firebase/firestore'
import { productDb } from '@/shared/firebase/client'
import { PRODUCT_COLLECTIONS } from '@/shared/firebase/collections'

const usersRef = () => collection(productDb, PRODUCT_COLLECTIONS.users)

async function countWhere(...clauses: Parameters<typeof where>[]): Promise<number> {
  const q =
    clauses.length === 0 ? query(usersRef()) : query(usersRef(), ...clauses.map((c) => where(...c)))
  const snap = await getCountFromServer(q)
  return snap.data().count
}

export interface AnalyticsSnapshot {
  totalUsers: number
  /** Null when the range is all-time. */
  newInPeriod: number | null
  /** Entitlements that have not expired — paying and trialling together. */
  premiumActive: number
  /** Of those, on a paid period rather than a trial. */
  premiumPaying: number
  premiumTrial: number
  /** Expired entitlements. Everyone who has ever held Premium is active + lapsed. */
  premiumLapsed: number
  /** Live subscriptions the customer has already cancelled — paid up, will not renew. */
  premiumNotRenewing: number
  /**
   * Set to auto-renew, yet the mirrored expiry has already passed.
   *
   * These almost certainly renewed and this copy has not caught up: the map is
   * refreshed when the app syncs, so a customer who has not opened Skida since
   * their renewal still shows the old date. Surfaced as a data-quality figure —
   * it is the size of the gap between this dashboard and RevenueCat.
   */
  premiumStaleSuspect: number
  /** Signups per week, oldest first. Null when the range is all-time. */
  weeklySignups: { weekStart: string; count: number }[] | null
}

/** Field path for the only thing that decides whether Premium is live. */
const EXPIRES = 'activeEntitlements.Premium.expirationDateMillis'
const PERIOD = 'activeEntitlements.Premium.periodType'
/** RevenueCat's auto-renew flag, mirrored onto the user. */
const WILL_RENEW = 'activeEntitlements.Premium.willRenew'

/**
 * @param days Period for the period-scoped metrics, or null for all-time.
 * @param now  Injected so the caller controls "today" and the result is testable.
 */
export async function loadAnalytics(
  days: number | null,
  now: number = Date.now(),
): Promise<AnalyticsSnapshot> {
  const since = days === null ? null : now - days * 86_400_000

  const [
    totalUsers,
    newInPeriod,
    premiumActive,
    premiumLapsed,
    premiumPaying,
    premiumTrial,
    premiumNotRenewing,
    premiumStaleSuspect,
  ] = await Promise.all([
    countWhere(),
    since === null ? Promise.resolve(null) : countWhere(['createdAt', '>=', since]),
    countWhere([EXPIRES, '>', now]),
    countWhere([EXPIRES, '<=', now]),
    countWhere([PERIOD, '==', 'NORMAL'], [EXPIRES, '>', now]),
    countWhere([PERIOD, '==', 'TRIAL'], [EXPIRES, '>', now]),
    countWhere([WILL_RENEW, '==', false], [EXPIRES, '>', now]),
    countWhere([WILL_RENEW, '==', true], [EXPIRES, '<=', now]),
  ])

  // Weekly series: one cheap count per bucket. Skipped for all-time, where the
  // bucket count would be unbounded.
  let weeklySignups: AnalyticsSnapshot['weeklySignups'] = null
  if (since !== null) {
    const weeks = Math.min(8, Math.ceil(days! / 7))
    const results = await Promise.all(
      Array.from({ length: weeks }, (_, i) => {
        const end = now - i * 7 * 86_400_000
        const start = end - 7 * 86_400_000
        return countWhere(['createdAt', '>=', start], ['createdAt', '<', end]).then((count) => ({
          weekStart: new Date(start).toISOString().slice(0, 10),
          count,
        }))
      }),
    )
    weeklySignups = results.reverse()
  }

  return {
    totalUsers,
    newInPeriod,
    premiumActive,
    premiumPaying,
    premiumTrial,
    premiumLapsed,
    premiumNotRenewing,
    premiumStaleSuspect,
    weeklySignups,
  }
}
